"use client";

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

// =============================================================================
// Vigia de versão: avisa (e, quando dá, recarrega sozinho) uma aba que ficou
// presa num build antigo. Por quê: a skew protection da Vercel mantém a aba no
// deploy em que ela abriu; router.refresh() e cliques na sidebar continuam
// batendo no build velho. Em 04/09/2026 isso fez "/progresso não abre" durar
// a tarde inteira depois de corrigido. Compara o data-dpl-id do HTML com o
// deploy que responde HOJE (fetch sem cookie = sem skew).
// =============================================================================

const INTERVALO_MS = 5 * 60_000;

async function deployAtual(): Promise<string | null> {
  try {
    const r = await fetch(`/api/health/versao?t=${Date.now()}`, { credentials: "omit", cache: "no-store" });
    if (!r.ok) return null;
    const j = (await r.json()) as { dpl?: string | null };
    return j.dpl ?? null;
  } catch {
    return null;
  }
}

function algumCampoEmEdicao(): boolean {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || (el as HTMLElement).isContentEditable);
}

export function VersaoVigia() {
  const [desatualizada, setDesatualizada] = useState(false);

  useEffect(() => {
    const local = document.documentElement.dataset.dplId ?? null;
    if (!local) return; // fora da Vercel (dev/local) não há o que comparar
    let parado = false;

    const checar = async (aoVoltar: boolean) => {
      const atual = await deployAtual();
      if (parado || !atual || atual === local) return;
      // Voltou pra aba e não está digitando: recarrega sem perguntar.
      if (aoVoltar && !algumCampoEmEdicao()) {
        window.location.reload();
        return;
      }
      setDesatualizada(true);
    };

    const timer = setInterval(() => void checar(false), INTERVALO_MS);
    const onVis = () => {
      if (!document.hidden) void checar(true);
    };
    document.addEventListener("visibilitychange", onVis);
    void checar(false);
    return () => {
      parado = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  if (!desatualizada) return null;
  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-md border border-accent-cyan/40 bg-bg-panel px-4 py-2 text-xs text-text-primary shadow-lg"
    >
      <span>Nova versão da Skynet no ar. Esta aba ainda está na anterior.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1.5 rounded-md border border-accent-cyan/40 px-2.5 py-1 font-medium text-accent-cyan hover:bg-accent-cyan/10"
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
        Recarregar
      </button>
    </div>
  );
}
